/**
 * Design tokens for the app surface (State / Directions / …).
 *
 * Values are the handoff's, which were themselves lifted from
 * `src/components/dashboard/editorial/tokens.ts`. Kept as a separate module
 * because the two surfaces diverge: the editorial dashboard's `scoreColor`
 * carries English zone names and a different breakpoint set, and the app needs
 * the engine-inverted pattern scale that the old file only half-implements.
 *
 * Colour here is a SCALE, never decoration: every coloured pixel means a
 * measured number. No number → no colour (see `scoreColor(null)`).
 */

export const c = {
    bg: '#0a0a0a',
    card: '#121110',
    cardIn: '#0e0d0c',
    pill: '#16140f',
    text: '#e8e6e0',
    textPri: '#f4f2ee',
    muted: 'rgba(232,230,224,0.55)',
    dim: 'rgba(232,230,224,0.40)',
    subtle: 'rgba(232,230,224,0.28)',
    track: 'rgba(232,230,224,0.06)',
} as const;

export const hair = '1px solid rgba(232,230,224,0.08)';
export const hair2 = '1px solid rgba(232,230,224,0.04)';

export const f = {
    ui: "'Inter Tight', system-ui, sans-serif",
    mono: "'JetBrains Mono', monospace",
    serif: "'Fraunces', Georgia, serif",
} as const;

/**
 * Accent for input controls. Deliberately CONSTANT — a report form is an action,
 * not a measurement, so it never borrows the pattern's zone colour. Otherwise a
 * task whose pattern is still a hypothesis would render as disabled.
 */
export const ACT = '#fb923c';

export interface Zone {
    hex: string;
    soft: string;
    /**
     * Translation key for the verdict, not the verdict itself — this module is
     * imported outside React, so it cannot resolve one. Render with `t(z.key)`.
     */
    key: string;
}

/** Score scale — orb, rings, trends, direction bars. */
const SCORE: Array<Zone & { max: number }> = [
    { max: 40, hex: '#ef4444', soft: '#ef444422', key: 'app.zone.critical' },
    { max: 65, hex: '#f59e0b', soft: '#f59e0b22', key: 'app.zone.fair' },
    { max: 85, hex: '#84cc16', soft: '#84cc1622', key: 'app.zone.good' },
    { max: 101, hex: '#10b981', soft: '#10b98122', key: 'app.zone.excellent' },
];

export const SCORE_ZONES: ReadonlyArray<Zone & { max: number }> = SCORE;

/** `null` means "not measured" — renders neutral, never a guessed colour. */
export const scoreColor = (s: number | null | undefined): Zone =>
    s == null
        ? { hex: c.dim, soft: 'transparent', key: 'app.zone.locked' }
        : SCORE.find((z) => s < z.max) || SCORE[SCORE.length - 1];

export type Polarity = 'shadow' | 'engine';

/** Pattern-strength scale. */
const ZONE: Array<Zone & { min: number }> = [
    { min: 80, hex: '#f87171', soft: '#f8717118', key: 'app.zone.critical' },
    { min: 60, hex: '#fb923c', soft: '#fb923c18', key: 'app.zone.fair' },
    { min: 40, hex: '#facc15', soft: '#facc1518', key: 'app.zone.good' },
    { min: 0, hex: '#4ade80', soft: '#4ade8018', key: 'app.zone.excellent' },
];

/**
 * Colour answers "how good is this", not "how much of it is there". For an
 * engine pattern the value is inverted first, so a weak engine reads red and a
 * weak blocker reads green.
 *
 * `effectiveStrength` exists so bar length and list order use the same inverted
 * value — otherwise a sorted list argues with its own colours.
 */
export const effectiveStrength = (pct: number, pol: Polarity = 'shadow'): number =>
    pol === 'engine' ? 100 - pct : pct;

export const patternZone = (pct: number, pol: Polarity = 'shadow'): Zone => {
    const eff = effectiveStrength(pct, pol);

    return ZONE.find((z) => eff >= z.min) || ZONE[ZONE.length - 1];
};

/**
 * Translation key for the zone verdict — same wording for blockers and engines,
 * since both answer "how good". Render with `t(strengthKey(...))`.
 */
export const strengthKey = (pct: number, pol: Polarity = 'shadow'): string => patternZone(pct, pol).key;

/** Cognitive-load labels, by translation key. */
export const LOAD: Record<string, string> = {
    low: 'app.load.low',
    medium: 'app.load.medium',
    high: 'app.load.high',
};

export const DIR_ORDER = [
    'cognition',
    'affect',
    'attention',
    'self',
    'motivation',
    'behavior',
    'biophysiology',
    'social',
] as const;

/** Monday-first weekday keys — see `tasks.ts` for why the week starts there. */
export const DAY_KEYS = [
    'app.day.mon',
    'app.day.tue',
    'app.day.wed',
    'app.day.thu',
    'app.day.fri',
    'app.day.sat',
    'app.day.sun',
];

/**
 * Points a completed day of an exercise is worth, from its length. How that
 * value is divided between patterns and directions is `splitPoints`'s job —
 * this is only the price of the day.
 */
export const xpBase = (min: number | null): number => {
    const m = min ?? 5;

    if (m <= 2) return 10;
    if (m <= 5) return 20;
    if (m <= 15) return 35;
    return 50;
};

/** Desktop breakpoint — below it the tab bar shows, above it the sidebar. */
export const DESKTOP = 1000;
